// R1b-β Step 1 Deliverable C — Unified `/admin/settings` shell.
//
// Every card is either a deep-link to an existing live surface OR an
// honest PhasePlaceholder (never a fake metric or fake action). Rendered
// via AdminAppShell with activeNav="people-access" — the People & Access
// primary-nav group highlights when the user is on /admin/settings.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { Card, CardContent } from "../../components/ui/card";
import {
  Building2, Palette, MapPin, Bell, Shield, Database, Lock, Plug,
  Hammer, Calculator, LayoutDashboard, ToggleLeft, GraduationCap,
  ArrowUpRight, Info, RotateCcw, CreditCard, Network,
} from "lucide-react";

const LIVE = "live";
const PHASE = "not_implemented";  // B2a: these editors are not built (no roadmap phase is promised)

const SECTIONS = [
  { key: "company-profile", title: "Company Profile", icon: Building2, kind: LIVE,
    to: "/admin/tenants", body: "Legal name, contact, states of operation, marketing copy. Owner + Administrator editable." },
  { key: "branding", title: "Branding", icon: Palette, kind: PHASE, phase: 4,
    to: "/admin/website", secondaryLabel: "Public-site content editor",
    body: "Logo, colours, typography for internal chrome. Public marketing site content already editable. A brand-token editor for internal chrome is not implemented." },
  { key: "locations", title: "Locations & States of Operation", icon: MapPin, kind: PHASE, phase: 4,
    body: "States of operation currently live on the tenant profile (view only in Company Profile). A full locations and service-area editor is not implemented." },
  { key: "notification-prefs", title: "Notification Preferences", icon: Bell, kind: LIVE,
    to: "/admin/notification-templates",
    body: "Notification templates are live. Per-user notification preferences (channel + frequency) are not implemented." },
  { key: "approvals-config", title: "Approvals Configuration", icon: Shield, kind: LIVE,
    to: "/admin/people-roles/authority",
    body: "Authority Matrix rules — who can approve what, at what dollar band, in which state." },
  { key: "retention", title: "Data Retention", icon: Database, kind: PHASE, phase: 6,
    body: "Retention windows for documents / audit / user-data are seeded on tenant creation. A retention editor is not implemented." },
  { key: "modules", title: "Modules", icon: ToggleLeft, kind: LIVE, to: "/admin/settings/modules", ownerOnly: true,
    body: "Founder only. Every module's real state, dependencies and switch. Not implemented and externally blocked modules can't be activated. Changes need a step-up and are audited." },
  { key: "ai_usage", title: "AI usage & limits", icon: Lock, kind: LIVE, to: "/admin/settings/ai-usage", ownerOnly: true,
    body: "Founder only. Month-to-date AI spending by feature against the monthly limit ($100 default on the Universal Key). Warnings at 50% and 80%; hard stop at the limit. Changes need a step-up and are audited." },
  { key: "security", title: "Sign-in security policy", icon: Lock, kind: LIVE,
    to: "/admin/settings/sign-in-security", ownerOnly: true,
    body: "Founder only. Require two-step sign-in (passkey or authenticator app) per role, with a grace period. Default OFF; changes need a step-up and are audited. Password rotation is live; session-timeout policy and IP allowlists are not implemented." },
  { key: "project-access", title: "Project Access", icon: Lock, kind: LIVE,
    to: "/admin/settings/project-access",
    body: "Founder only. Assignment-based field access (default OFF) with the dry-run summary. Company-wide PM visibility is set on each PM's user page." },
  { key: "integrations", title: "Integrations Status", icon: Plug, kind: LIVE,
    to: "/admin/integrations",
    body: "Twenty-two integration adapters — Stripe / SendGrid / Twilio / MS 365 / QuickBooks / Xero / Plaid / DocuSign / and more. Connect, verify, rotate, disconnect." },
  { key: "construction-config", title: "Construction Configuration", icon: Hammer, kind: LIVE,
    to: "/admin/trade-catalog",
    body: "Trade catalogue + service lines already visible tenant-scoped. A cost-codes editor is not implemented." },
  { key: "platform-billing", title: "Platform Billing", icon: CreditCard, kind: LIVE,
    to: "/admin/billing",
    body: "Your ERF ConstructionOS subscription, platform invoices, payment method, and entitlement status. ERF Founder is paywall-immune." },
  { key: "accounting-readiness", title: "Accounting Readiness", icon: Calculator, kind: PHASE, phase: 5,
    body: "QuickBooks and Xero adapters exist but are not yet configured for any tenant. QuickBooks / Xero sync is externally blocked (provider not contracted)." },
  { key: "portal-config", title: "Portal Configuration", icon: LayoutDashboard, kind: PHASE, phase: 5,
    body: "Client-portal branding + module toggles. Portal shell exists (/client/portal); a configuration surface is not implemented." },
  { key: "feature-flags", title: "Feature Flags", icon: ToggleLeft, kind: LIVE,
    to: "/admin/feature-flags", ownerOnly: true,
    body: "Per-tenant feature toggles. Owner-only." },
  { key: "tutorial-help", title: "Tutorial & Help", icon: GraduationCap, kind: "tutorial",
    body: "Restart the first-run product tour or visit the Help Center." },
];

function SectionCard({ section, isOwner }) {
  const Icon = section.icon;
  const navigate = useNavigate();
  const disabled = section.ownerOnly && !isOwner;

  const chip = section.kind === PHASE ? (
    <span
      data-testid={`admin-settings-phasen-${section.key}`}
      className="text-[10px] font-mono uppercase tracking-widest text-erf-gold border border-erf-gold/40 rounded-sm px-1.5 py-0.5"
    >
      Phase {section.phase}
    </span>
  ) : section.kind === LIVE ? (
    <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 border border-emerald-700/40 rounded-sm px-1.5 py-0.5">
      Live
    </span>
  ) : null;

  const isTutorial = section.kind === "tutorial";
  return (
    <Card
      className="border-erf-borderCream bg-erf-cream h-full"
      data-testid={`admin-settings-section-${section.key}`}
    >
      <CardContent className="p-5 flex flex-col gap-3 h-full">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <Icon className="w-4 h-4 text-erf-gold mt-0.5 shrink-0" aria-hidden="true" />
            <div className="font-display font-semibold text-erf-dark leading-tight">{section.title}</div>
          </div>
          {chip}
        </div>
        <div className="text-xs text-erf-dark/70 font-nav leading-relaxed flex-1">
          {section.body}
        </div>
        {section.kind === PHASE && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
            <span>Not Implemented</span>
          </div>
        )}
        <div className="flex items-center gap-3 pt-1 mt-auto flex-wrap">
          {isTutorial ? (
            <>
              <button
                type="button"
                onClick={() => navigate("/owner?tour=restart")}
                data-testid={`admin-settings-link-${section.key}`}
                className="inline-flex items-center gap-1 text-xs font-nav text-erf-gold hover:underline"
              >
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Restart Owner tour
              </button>
              <Link
                to="/help"
                data-testid={`admin-settings-link-${section.key}-help`}
                className="inline-flex items-center gap-1 text-xs font-nav text-erf-dark hover:underline"
              >
                Help Center <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
              </Link>
            </>
          ) : section.to ? (
            <Link
              to={disabled ? "#" : section.to}
              onClick={disabled ? (e) => e.preventDefault() : undefined}
              data-testid={`admin-settings-link-${section.key}`}
              className={"inline-flex items-center gap-1 text-xs font-nav " +
                (disabled ? "text-erf-dark/30 cursor-not-allowed" : "text-erf-gold hover:underline")}
            >
              Open <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
              {section.secondaryLabel ? <span className="text-erf-dark/60 ml-1">({section.secondaryLabel})</span> : null}
            </Link>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsShell() {
  const { user } = useAuth();
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";
  const isPlatformOperator = user?.role_key === "PLATFORM_OPERATOR";
  return (
    <AdminAppShell
      activeNav="people-access"
      title="Settings"
      subtitle="Tenant configuration · consolidated"
      breadcrumbs={[
        { label: "People & Access", to: "/admin/people-roles/users" },
        { label: "Settings", to: null },
      ]}
      user={user}
    >
      <div
        className="mx-auto w-full max-w-[1600px] box-border px-4 md:px-6 lg:px-8 space-y-6"
        data-testid="admin-settings-shell"
      >
        {isPlatformOperator && (
          <Card className="border-erf-gold/60 bg-erf-cream" data-testid="admin-settings-cross-tenant-admin">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-sm bg-erf-gold/10 border border-erf-gold/40 flex items-center justify-center shrink-0">
                    <Network className="w-5 h-5 text-erf-goldHover" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Platform Operator</div>
                    <div className="font-display font-semibold text-erf-dark mt-0.5">Cross-Tenant Administration</div>
                    <p className="text-xs font-nav text-erf-dark/70 mt-1 leading-relaxed max-w-xl">
                      Manage tenant directory, suspension state, and billing exemptions across every tenant on this cluster.
                      Every mutation is audit-logged at elevated severity.
                    </p>
                  </div>
                </div>
                <Link
                  to="/admin/tenants"
                  data-testid="admin-settings-cross-tenant-link"
                  className="inline-flex items-center gap-1 text-xs font-nav text-erf-gold hover:underline mt-1"
                >
                  Open All Tenants <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTIONS.map((s) => <SectionCard key={s.key} section={s} isOwner={isOwner} />)}
        </div>
      </div>
    </AdminAppShell>
  );
}
